import { useCallback, useEffect, useRef } from 'react';
import tw from 'twin.macro';

import { AdjustmentsHorizontalIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';

import Select from '@/components/base/Select';

import { CandidateDashboardFilters } from '../types';

const FilterBar = ({
  filters,
  onChange,
  positions,
  templates,
}: {
  filters: CandidateDashboardFilters;
  onChange: (f: CandidateDashboardFilters) => void;
  positions: { value: string; label: string }[];
  templates: { value: string; label: string }[];
}) => {
  const searchTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const filtersRef = useRef(filters);

  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  useEffect(() => {
    return () => clearTimeout(searchTimerRef.current);
  }, []);

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      clearTimeout(searchTimerRef.current);
      searchTimerRef.current = setTimeout(() => {
        onChange({ ...filtersRef.current, search: value });
      }, 300);
    },
    [onChange],
  );

  const handlePositionChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onChange({ ...filters, position: e.target.value || null });
  };

  const handleTemplateChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onChange({ ...filters, questionTemplateID: e.target.value || null });
  };

  return (
    <div tw="grid grid-cols-12 gap-3 items-center">
      <div tw="col-span-12 lg:col-span-5 relative">
        <MagnifyingGlassIcon tw="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" />
        <input
          type="text"
          placeholder="ค้นหาด้วยชื่อ, อีเมล, หรือ Ref ID..."
          defaultValue={filters.search}
          onChange={handleSearchChange}
          tw="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 transition-all placeholder:text-gray-400"
        />
      </div>
      <div tw="col-span-6 lg:col-span-3">
        <Select value={filters.position ?? ''} onChange={handlePositionChange} tw="w-full rounded-xl">
          <option value="">ทุกตำแหน่งงาน</option>
          {positions.map((p) => (
            <option key={p.value} value={p.value}>
              {p.label}
            </option>
          ))}
        </Select>
      </div>
      <div tw="col-span-6 lg:col-span-3">
        <Select
          value={filters.questionTemplateID ?? ''}
          onChange={handleTemplateChange}
          tw="w-full rounded-xl"
        >
          <option value="">ทุกชุดคำถาม</option>
          {templates.map((t) => (
            <option key={t.value} value={t.value}>
              {t.label}
            </option>
          ))}
        </Select>
      </div>
      <div tw="col-span-12 lg:col-span-1">
        <button
          type="button"
          tw="w-full min-h-[44px] flex items-center justify-center bg-amber-500 text-white rounded-xl hover:bg-amber-600 transition-colors"
        >
          <AdjustmentsHorizontalIcon tw="h-5 w-5" />
        </button>
      </div>
    </div>
  );
};

export default FilterBar;
